import { getMessageWithPlaceholders } from 'shared-locales';
import { useReduxState } from 'hooks';
import api from 'api';
import { ConfirmButton } from 'components/common';
import { Dropdown, DropdownButton } from 'components/common/dropdown';

import { useFiltersContext } from './FilterContext';

import type { UserSavedFilter } from 'shared-types';

interface Props {
  onApplyFilter: (filterQuery: string) => void;
}

/**
 * Button component that displays a dropdown with user's saved filters
 * Shows the number of saved filters and allows applying or deleting them
 *
 * @param onApplyFilter - Callback function when a filter is selected
 */
const SavedFiltersButton = ({ onApplyFilter }: Props) => {
  const { messages, theme } = useReduxState();

  const { savedFilters, savedFiltersUpdate } = useFiltersContext();

  const hasSavedFilters = savedFilters.length > 0;

  const handleFilterSelect = (filter: UserSavedFilter) => {
    onApplyFilter(filter.filterQuery);
  };

  const getFilterCount = (filterQuery: string) => {
    const params = new URLSearchParams(filterQuery);
    return params.size;
  };

  const handleDeleteFilter = async (filterId: number) => {
    const response = await api.delete(`/admin/saved-filters/${filterId}`);

    if (response.data?.success) {
      await savedFiltersUpdate();
      return true;
    }

    return false;
  };

  return (
    <Dropdown
      align="right"
      category="lighter"
      icon="bookmark"
      iconStyle={{
        fontSize: '1rem',
        marginRight: savedFilters.length > 0 ? 5 : 0
      }}
      isMenu={true}
      label={messages.savedFiltersTooltip}
      styleContentButtons={false}
      title={
        <>
          <div className="filters-text">
            {hasSavedFilters && (
              <div className="filters-count">{savedFilters.length}</div>
            )}
          </div>
        </>
      }
      tooltip={messages.savedFiltersTooltip}
    >
      {!hasSavedFilters && (
        <div className="empty-indicator">{messages.savedFiltersNoOptions}</div>
      )}

      {hasSavedFilters &&
        savedFilters.map((filter) => (
          <div key={filter.id} className="filter-item">
            <DropdownButton
              onClick={() => handleFilterSelect(filter)}
              text={filter.name}
              subText={getMessageWithPlaceholders({
                message: messages.usersFiltersAppliedCount,
                placeholders: { count: getFilterCount(filter.filterQuery) }
              })}
              style={{ paddingRight: '50px', maxWidth: '400px' }}
            />
            <div className="delete-button">
              <ConfirmButton
                actionText={messages.commonDelete}
                buttonIcon="trash-alt"
                buttonSize="small"
                category="danger"
                buttonCategory="transparent"
                confirmText={getMessageWithPlaceholders({
                  message: messages.savedFiltersConfirmDelete,
                  placeholders: { name: filter.name }
                })}
                confirmTitle={messages.savedFiltersConfirmDeleteTitle}
                onConfirm={() => handleDeleteFilter(filter.id)}
                successMessage={messages.savedFiltersDeleteSuccess}
                stopPropagation={true}
              />
            </div>
          </div>
        ))}

      <style jsx>{`
        .empty-indicator {
          padding: 8px 15px;
          color: #888;
          font-size: 1.4rem;
          min-width: 150px;
          white-space: nowrap;
        }

        .filters-text {
          align-items: center;
          display: inline-flex;
          gap: 7px;
        }

        .filters-count {
          background-color: white;
          border-radius: 20px;
          color: ${theme.textColorLight};
          font-size: 1.2rem;
          font-weight: 700;
          margin: -2px 0;
          padding: 2px 0;
          position: relative;
          top: -1px;
          text-align: center;
          height: 20px;
          width: 20px;
        }

        .filter-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          position: relative;
        }

        .delete-button {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
        }
      `}</style>
    </Dropdown>
  );
};

export default SavedFiltersButton;
